import { get_db_base, request_items } from "./DB";
import { reportFrontendError } from "./generator/Amplify";

/**
 * Removes leading & trailing (given) tags
 */
export const remove_wrapping_tag = (str, tag) => {
  if (!str) return "";
  // remove wrapping tag
  const regexp_leading = new RegExp("^<" + tag + ".*?>");
  const regexp_trailing = new RegExp("</" + tag + ".*?>$");

  return str.replace(regexp_leading, "").replace(regexp_trailing, "");
};

/**
 * Removes leading & trailing P tags
 */
export const remove_p_tag = (str) => remove_wrapping_tag(str, "p");

/**
 * Removes all tags
 */
export const remove_all_tags = (str) =>
  (str || "").replace(/<([^ ]+) ?.*?>.*<\/\1>/g, "");

/**
 * Returns the index of the given .sheet element within its .sheet siblings
 */
export const index_of_sheet = (sheetId) => {
  var target_sheet = document.querySelector(sheetId);
  if (!target_sheet) {
    console.error("target_sheet for: " + sheetId);
    return 0;
  }

  var sheets_container = target_sheet.parentNode;
  if (!sheets_container) {
    console.error("sheets_container for: " + sheetId);
    return 0;
  }

  var all_sheets = [...sheets_container.querySelectorAll(".sheet")];
  var target_sheet_idx = all_sheets.indexOf(target_sheet);
  if (target_sheet_idx === -1) {
    console.error("target_sheet_idx for: " + sheetId);
    return 0;
  }
  return target_sheet_idx;
};

export { get_db_base, request_items };

export const request_items_ex = (
  db_base,
  collection,
  collection_name_override,
  is_single,
  fields_to_get,
  filter
) => {
  // var obj = request_items(db_base, collection, collection_name_override, is_single, fields_to_get, filter);
  // return obj[Object.keys(obj)[0]];
  var collection_name = collection_name_override
    ? collection_name_override
    : collection;
  return new Promise((a, r) => {
    request_items(
      db_base,
      collection,
      collection_name_override,
      is_single,
      fields_to_get,
      filter
    ).then((result) => {
      let obj = {};
      obj[collection_name] = result;
      a(obj);
    });
  });
};

/**
 * Trims values from a list of objects
 *
 * For example: { a: ' b ' } -> { a: 'b' }
 */
export const trim_object_list_string_values = (list) => {
  list.forEach((item) => {
    for (var key in item) {
      if (typeof item[key] === "string") item[key] = item[key].trim();
    }
  });
  return list;
};

/**
 * Polyfill replacement for String.endsWith
 */
export const str_ends_with = (str, ending) => {
  return str.substring(str.length - ending.length, str.length) === ending;
};

export const set_page_title = (title) => (document.title = title);

export const is_set = (v) => !(v === null || v === undefined);

/**
 * Verifies whether a given string could possibly be bundle ID.
 */
export const isIdBundle = (str) => {
  var intVal = parseInt(str);
  if (isNaN(intVal)) return true;
  // Bundle ID may be composed of some integers.
  // We know the string is a bundle ID if the length of the int value
  // is not equivalent to the length of the string itself
  return ("" + intVal).length !== ("" + str).length;
};

/**
 * Condensed way to simply provide value or default based on a given condition result
 */
export const getOrDefault = (obj, key, key_default, should_inherit_default) => {
  if (should_inherit_default || !key) return obj[key] || obj[key_default];
  return obj[key];
};

/**
 * Returns the value of a pivoted field
 * For example: audienced+localized fields
 *
 * param examples:
 *  - obj: {id, audience}
 *  - field: "heading"
 *  - pivots/default_pivots:
 *  - - for localized:
 *  - - - pivots = {locale: selected_locale}
 *  - - - default_pivots = {locale: default_locale.language}
 *  - - for audienced, localized:
 *  - - - pivots = {audience: selected_audience, locale: selected_locale}
 *  - - - default_pivots = {audience: default_audience, locale: default_locale.language}
 */
export function getLocalizedOrDefault(obj, field, pivots, default_pivots) {
  const pivot_keys = Object.keys(pivots);
  // 1=only locale, 2=audience & locale
  if (pivot_keys.length === 1) {
    // return a localized field based on the selected locale language
    // if no field exists, get the localized field based on the default locale language
    const locale = obj.locale || [];
    const current_locale_value = locale.find((locale) => {
      const code =
        locale?.language?.language ||
        locale?.language?.code ||
        locale?.languages_code;
      return (
        code === pivots.locale ||
        code === pivots.locale?.language ||
        code === pivots.language
      );
    })?.[field];
    const default_locale_value = locale.find((locale) => {
      const code =
        locale?.language?.language ||
        locale?.language?.code ||
        locale?.languages_code;
      return (
        code === default_pivots.locale ||
        code === default_pivots.locale?.language ||
        code === default_pivots.language
      );
    })?.[field];
    if (Array.isArray(current_locale_value)) {
      if (current_locale_value.length > 0) {
        return current_locale_value;
      }
      return default_locale_value;
    }
    return current_locale_value || default_locale_value;
  } else {
    //pivot by (default?) audience and then again by (default) locale
    return (
      getPivotedField(obj, field, pivots) ||
      getPivotedField(obj, field, default_pivots)
    );
  }
}

/**
 * Localizes multiple fields on an object in place, using `_fieldname` backing values.
 *
 * Each element of `fields` is either:
 *   - a string key: resolved value is written directly to obj[key]
 *   - { key, transform }: resolved value is passed through transform() before writing
 *
 * The resolved value for key is:
 *   getLocalizedOrDefault(obj, key, p, d) ?? obj[`_${key}`]
 */
export function localizeFields(
  obj,
  fields,
  selectedLocale,
  defaultLocale = "en-US"
) {
  const p = { locale: selectedLocale };
  const d = { locale: defaultLocale };
  for (const spec of fields) {
    const key = typeof spec === "string" ? spec : spec.key;
    const resolved = getLocalizedOrDefault(obj, key, p, d) ?? obj[`_${key}`];
    obj[key] = typeof spec === "string" ? resolved : spec.transform(resolved);
  }
}

/**
 * Returns the value of a pivoted field
 * For example: audienced+localized fields
 *
 * param examples:
 *  - obj: {id, audience}
 *  - field:
 *  - pivots: { selected_audience, selected_locale }
 */
export function getPivotedField(obj, field, pivots) {
  const pivot_keys = Object.keys(pivots);
  let value = obj;
  pivot_keys.forEach((key) => (value = value?.[key]?.[pivots[key]]));
  return value?.[field];
}

/**
 * Retrieve audienced, localized content
 *
 * Params:
 *  - obj - data container
 *  - key_default_pairs - list of objects in the form [{key, key_default}],
 *                        where key is the "currently selected" value for a setting
 *                        (e.g. audiences), and key_default is the default key to use if
 *                        the value for a key is not present
 *  - should_inherit_default - whether to inherit defaults if primary value not present
 */
export const getOrDefault_V2 = (
  obj,
  key_default_pairs,
  should_inherit_default
) => {
  let prev_value = obj;
  for (var k in key_default_pairs) {
    const { key, key_default } = key_default_pairs[k];

    prev_value = getOrDefault(
      prev_value,
      key,
      key_default,
      should_inherit_default
    );
  }

  return prev_value;
};

const dt_class = 'class="dynamic-text"';
const span_dt_pfx = `<span ${dt_class}>`;
// Find any nested tags such as <sup>1</sup>
const tag_match_regex = /<(\w+)[^>]*>([^<]*)<\/\1>/g;
/**
 * Converts text with html content in it into siblings of class .dynamic-text
 *
 * For example,
 *   "PCL<sup>2</sup> and PostScript"
 *  becomes
 *   <span class="dynamic-text">PCL</span><sup class="dynamic-text">2</sup>
 *     <span class="dynamic-text">and PostScript</span>
 */
export const intersperseDynamicText = (string = "") => {
  const replacement = `<$1 ${dt_class}>$2</$1>`;
  const processed_content = string
    // Here we would have "Lowest in its Class<sup class="dynamic-text">1</sup>"
    .replace(tag_match_regex, replacement);

  const pc2 = processed_content
    // Add a </span> before a .dynamic-text, and an opening one after it
    .replace(tag_match_regex, (match) => `</span>${match}${span_dt_pfx}`);

  // Finally, add missing open/closing tags at beginning/end
  return `${span_dt_pfx}${pc2}</span>`;
};

const unicodeForSuperscriptSymbol = (digit) => {
  switch (digit) {
    case "0":
      return "\u2070";
    case "1":
      return "\u00B9";
    case "2":
      return "\u00B2";
    case "3":
      return "\u00B3";
    case "4":
      return "\u2074";
    case "5":
      return "\u2075";
    case "6":
      return "\u2076";
    case "7":
      return "\u2077";
    case "8":
      return "\u2078";
    case "9":
      return "\u2079";
    default:
      return digit;
  }
};

/**
 * Converts superscripts within a string to an equivalent unicode character,
 * if possible
 *
 * For example,
 *   "PCL<sup>2</sup> and PostScript"
 *  becomes
 *   PCL² and PostScript
 */
export const digitSuperscriptsToUnicode = (string = "") => {
  const matches = [...string.matchAll(/<sup>(.)<\/sup>/g)] || [];
  matches.forEach(([entire_match, digit]) => {
    const replacement = unicodeForSuperscriptSymbol(digit);
    string = string.replace(entire_match, replacement);
  });
  return string;
};

export const findGlobalImageUrl = (collections = {}, image_name = "") => {
  const { global_settings } = collections;
  const { global_images = [] } = global_settings;
  const image = global_images.find(({ name }) => name === image_name);
  const image_url = image?.url || "";
  return image_url;
};

/**
 * Formats numbers with the appropriate decimal separator.
 *
 * number - the number to format
 * locale - string such as 'en-US' for delegating to toLocaleString
 * decimal_separator - a string override ([",", "."]) for specifying
 *                    a different separator. Useful for en-CB since
 *                    it is formatted as 'en-US' (1,000.000) but we
 *                    want to show 'es' formatting (1.000,000)
 */
export const localizeNumber = (number, locale = "en-US", decimal_separator) => {
  if (decimal_separator === ",") {
    return formatNumber(number, { whole_separator: ".", decimal_separator: "," });
  }
  if (decimal_separator === ".") {
    return formatNumber(number, { whole_separator: ",", decimal_separator: "." });
  }
  // "Locale Default" — infer separators from the locale
  const parts = new Intl.NumberFormat(locale).formatToParts(1000.1);
  const whole_separator = parts.find((p) => p.type === "group")?.value ?? ",";
  const detected_decimal = parts.find((p) => p.type === "decimal")?.value ?? ".";
  return formatNumber(number, { whole_separator, decimal_separator: detected_decimal });
};

/**
 * Formats numbers with the appropriate decimal separator.
 *
 * number - the number to format
 * options
 *   whole_separator - separator for whole number, such as ','
 *   decimal_separator - separator between decimal and whole number, such as '.'
 *   max_decimals - truncate (not round) decimal digits
 */
export const formatNumber = (number, options = {}) => {
  const {
    whole_separator = ",",
    decimal_separator = ".",
    max_decimals = 2
  } = options;

  const num = Number(number);
  if (number == null || isNaN(num) || !isFinite(num))
    return String(number ?? "");

  // Truncate decimal via string to avoid float precision issues
  const [whole_str, dec_str = ""] = String(Math.abs(num)).split(".");
  const truncated_dec = dec_str.substring(0, max_decimals).replace(/0+$/, "");

  // Intl.NumberFormat handles whole-number grouping and edge cases
  const grouped_whole = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
    useGrouping: true
  })
    .format(parseInt(whole_str, 10))
    .replace(/,/g, whole_separator);

  const sign = num < 0 ? "-" : "";

  if (!truncated_dec) return `${sign}${grouped_whole}`;
  return `${sign}${grouped_whole}${decimal_separator}${truncated_dec}`;
};

export function toSlug(str) {
  return str ? `${str.toLowerCase().replace(/ /g, "-").trim()}` : "";
}

/**
 * Returns whether the current environment is production.
 * This only checks NODE_ENV for `development` so that
 * `staging` can be used elsewhere.
 *
 * We do not immediately check for "production" in order
 * to take stage into account.
 */
export function isTargetEnvProduction() {
  const stage = process.env.REACT_APP_TARGET_STAGE;
  const nodeEnvIsDev = process.env.NODE_ENV === "development";
  return !(stage === "development" || stage === "staging" || nodeEnvIsDev);
}

export const goToTop = () => {
  document.body.scrollTop = document.documentElement.scrollTop = 0;
};
//determines if a new error should be added (ignores same every in sibling components)
export function shouldAddError(errors, newError) {
  const { errorMessage, stackTrace } = newError;
  return errors.every(
    (x) => x.errorMessage !== errorMessage && x.stackTrace !== stackTrace
  );
}

// Reports all unique errors to sns topic
export async function reportFrontendErrors(errors) {
  // Avoid locally
  if (!!process.env.REACT_APP_DISABLE_SNS) return;
  const seen = [];
  for (const e of errors) {
    if (!!shouldAddError(seen, e)) await reportFrontendError(e);
    seen.push(e);
  }
}

/**
 * Filter out falsy values
 */
export const cleanupArray = (array) => array.filter((v) => !!v);

//return the next multiple of n that comes after x (unless x is multiple of n)
export const getNextMultipleOfN = (x, n) => {
  return !(x % n) ? x : x + Math.abs((x % n) - n);
};

/**
 * Retrieves the date in the format: YYYY-MM-DD
 */
export function dateForPdfName() {
  var d = new Date();
  var year = d.getFullYear();
  var month = "" + (d.getUTCMonth() + 1);
  var day = "" + d.getUTCDate();
  return [year, zeroPad(month, 2), zeroPad(day, 2)].join("-");
}

/**
 * Creates a filename-safe shortname from a product
 */
export function formatProductModelForPdfName(product) {
  const shortname = makeProductShortnameForPdfName(product);
  return (
    (shortname || "")
      .toLowerCase()
      // Remove non-alphanumeric characters
      .replace(/[^a-z0-9]/g, "")
  );
}

/**
 * Pads a given string (of digits) with a given number of zeroes ("digits")
 */
function zeroPad(str, digits) {
  if (str.length < digits)
    for (var i = str.length; i < digits; ++i) str = "0" + str;
  return str;
}

/**
 * Extracts a model-variant pairing for PDF naming
 */
function makeProductShortnameForPdfName(product) {
  const { model = "", variant = "" } = product;
  if (model && variant) return `${model} ${variant}`;
  return model;
}

export function toTitleCase(str) {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function addYearToTemplate(template) {
  if (!template) return "";
  const date = new Date().toLocaleString().split(",")[0];
  const [month, day, year] = date.split("/").map((x) => parseInt(x));
  return template.replace(/{{year}}/g, year);
}

// ensure the protocol is present in a url string, adding https:// otherwise
export function ensureUrlHasProtocol(url) {
  if (!url) return "";
  if (!/^(?:https?:)?\/\//i.test(url)) {
    return `https://${url}`;
  }
  return url;
}
